import Tooltip from "@/ui/Tooltip"
import type { Placement } from "@floating-ui/react"
import type { RefObject } from "react"
// main's date sourcing (useHudDates) + this branch's hover-swap wiring.
import { useHudContext, useHudDates } from "../hud/shared/playbookHUDInfra"
import { METRIC_LABEL_BASELINE_TRIM_CLASS, useMetricHoverSwap } from "../hud/blocks/metrics/metricHoverSwap"
import { useMetricHoverTrigger } from "../hud/blocks/hoverSwapStickyZone"
import { useHoverSwapsDisabled } from "../hud/blocks/hoverSwapsDisabled"
import {
  MetricDefinitionPanel,
  useUnifiedHudPanels,
} from "../hud/blocks/panels/unifiedHoverPanels"
import { MetricTrigger } from "../hud/metric-details/MetricTrigger"
import { METRIC_DETAILS_HINT } from "../hud/metric-details/metricLabels"
import { getUnemploymentForDate } from "../plan-a/UnemploymentIndicators"
import { TickerNumber } from "./TickerNumber"

const EMPLOYMENT_TOOLTIP = <div>
  <p className="mb-2 font-bold font-mono uppercase"> Employment rate</p>
  <p>Percentage of Americans who have jobs.</p>
  <p className="mt-2">The official Unemployment Rate excludes people who aren't actively seeking work, so it understates job losses as AI displaces workers. We list the employment rate instead.</p>
  <p className="mt-2 border-t border-black/10 pt-1.5 font-sans text-[10px] text-[var(--race-background)]">{METRIC_DETAILS_HINT}</p>
</div>

export const EmploymentRate = ({ oneLine = false, valueFirst = false, labelFontSize, label, tooltipPlacement = "top-start", tooltipPositionRef }: { oneLine?: boolean; valueFirst?: boolean; labelFontSize?: string; label?: string; tooltipPlacement?: Placement; tooltipPositionRef?: RefObject<HTMLElement | null> }) => {
  const ctx = useHudContext()
  const { currentDate } = useHudDates()
  const dataDate = ctx.currentPhase?.date
    ? new Date(ctx.currentPhase.date)
    : currentDate
  // Branch plans (B/C/D/S) carry their own employment path on the phase.
  const branchRate = ctx.currentPhase?.employmentRatePct
  const displayedRate = Math.round(
    branchRate ?? getUnemploymentForDate(dataDate).rate
  )
  // Once the branch collapse bottoms out (<1%), employment stops being a
  // meaningful statistic — show a dash instead of a fake-precise 0%.
  const isIrrelevant = branchRate !== undefined && branchRate < 1
  // Inside a <MetricHoverSwap> provider the floating tooltip is suppressed:
  // hover is reported to the swap context instead, and a MetricHoverSwapTarget
  // elsewhere on the HUD renders this metric's definition in-canvas. The
  // MetricTrigger (click → details panel) stays either way.
  const metricSwap = useMetricHoverSwap()
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  // self-stretch/px-1.5 belong to the production stats row (HudStatsRow);
  // the px-1.5 is that row's continuous tooltip hover surface. The swap row
  // keeps the flush wrapper (the padding would indent the figures off the
  // policy column's edges), and so do hover-less surfaces (the mobile
  // dashboard): no tooltips there, and its narrow stat columns need the width.
  const wrapperClassName = valueFirst
    ? metricSwap || hoverSwapsDisabled
      ? "flex flex-col-reverse justify-end items-center w-min"
      : "flex flex-col-reverse justify-end items-center w-min self-stretch px-1.5"
    : oneLine
      ? "flex items-baseline justify-between gap-1.5"
      : "flex items-start flex-col"

  const fontSize = oneLine ? "text-[10.5px]" : "text-[8px]"
  const numberFontSize = oneLine || valueFirst ? "text-[11px]" : "text-[10.5px]"
  // Inside a <UnifiedHudPanels> the floating tooltip renders as the unified
  // panel card instead.
  const unifiedPanels = useUnifiedHudPanels()
  const metricHoverProps = useMetricHoverTrigger(
    "employment",
    isIrrelevant ? "—" : `${displayedRate}%`,
  )
  const trigger = (
    <MetricTrigger
      id="employment"
      className={wrapperClassName}
      {...metricHoverProps}
    >
      <div className={`${fontSize} ${valueFirst ? "text-center" : "whitespace-nowrap"} ${valueFirst && metricSwap ? METRIC_LABEL_BASELINE_TRIM_CLASS : ""}`} style={labelFontSize ? { fontSize: labelFontSize } : undefined}>
        {label ?? `Employment${oneLine ? " Rate" : ""}`}
      </div>
      <div className={`${numberFontSize} font-bold whitespace-nowrap ${valueFirst ? "" : ""}`}>
        {isIrrelevant
          ? "—"
          : (
            <>
              <TickerNumber
                value={displayedRate}
                format={{ maximumFractionDigits: 0 }}
              />
              <span className="ml-[2px]">%</span>
            </>
          )}
      </div>
    </MetricTrigger>
  )
  // Hover-less surfaces (the mobile dashboard): no definition tooltip — a
  // tap must go straight to the MetricTrigger's click (the details page)
  // instead of surfacing the hover affordance.
  if (metricSwap || hoverSwapsDisabled) return trigger
  return (
    <Tooltip
      placement={tooltipPlacement}
      positionRef={tooltipPositionRef}
      disableShadow={Boolean(unifiedPanels)}
      content={unifiedPanels ? (
        <MetricDefinitionPanel metricId="employment" />
      ) : (
        <div className="p-2 w-[280px]">{EMPLOYMENT_TOOLTIP}</div>
      )}
    >
      {trigger}
    </Tooltip>
  )
}
